import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import PersonContactHover from '../../components/PersonContactHover';
import {
  dailyLoginRows,
  dailyLoginTrend,
  loginDateRange,
  sortRows,
  trendWindowEnding,
} from '../../utils/userAnalytics';
import SortableTh from './SortableTh';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function formatDayLabel(ymd) {
  if (!ymd || ymd.length < 10) return ymd || '';
  const [, m, d] = ymd.split('-');
  return `${Number(m)}/${Number(d)}`;
}

function DailyLoginsTab({
  loginRows,
  activityDay,
  onActivityDayChange,
  userTypeFilter = '',
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('logins');
  const [sortDir, setSortDir] = useState('desc');
  const [trendOpen, setTrendOpen] = useState(false);

  const dateRange = useMemo(() => loginDateRange(loginRows), [loginRows]);

  const rows = useMemo(
    () => dailyLoginRows(loginRows, activityDay, { userTypeFilter }),
    [loginRows, activityDay, userTypeFilter]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.name.toLowerCase().includes(q)
      || r.userType.toLowerCase().includes(q)
      || r.facility.toLowerCase().includes(q)
      || r.speciality.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const sorted = useMemo(
    () => sortRows(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir]
  );

  const trendWindow = useMemo(
    () => trendWindowEnding(loginRows, activityDay, 30),
    [loginRows, activityDay]
  );

  const trendSeries = useMemo(
    () => dailyLoginTrend(loginRows, trendWindow.start, trendWindow.end, { userTypeFilter }),
    [loginRows, trendWindow.start, trendWindow.end, userTypeFilter]
  );

  const trendChart = useMemo(() => {
    const labels = trendSeries.map(p => formatDayLabel(p.day));
    return {
      data: {
        labels,
        datasets: [
          {
            label: 'Logins',
            data: trendSeries.map(p => p.logins),
            borderColor: 'rgb(13, 148, 136)',
            backgroundColor: 'rgba(13, 148, 136, 0.12)',
            fill: true,
            tension: 0.25,
            pointRadius: 2,
            pointHoverRadius: 5,
            yAxisID: 'y',
          },
          {
            label: 'Unique users',
            data: trendSeries.map(p => p.uniqueUsers),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
            fill: false,
            tension: 0.25,
            pointRadius: 2,
            pointHoverRadius: 5,
            borderDash: [4, 3],
            yAxisID: 'y1',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'top' },
          title: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                const i = items?.[0]?.dataIndex;
                return i != null && trendSeries[i] ? trendSeries[i].day : '';
              },
            },
          },
        },
        scales: {
          y: {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
            title: { display: true, text: 'Logins' },
            ticks: { precision: 0 },
          },
          y1: {
            type: 'linear',
            position: 'right',
            beginAtZero: true,
            grid: { drawOnChartArea: false },
            title: { display: true, text: 'Unique users' },
            ticks: { precision: 0 },
          },
        },
      },
    };
  }, [trendSeries]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'userType' || key === 'facility' ? 'asc' : 'desc');
    }
  };

  const platformTotal = (r) => r.web + r.mobile + r.desktop + r.goweb + r.telemedicine + r.common;

  return (
    <div className="users-tab-panel">
      <div className="users-tab-toolbar">
        <label className="users-field">
          <span className="users-field-label">Activity day</span>
          <input
            type="date"
            value={activityDay}
            min={dateRange.min || undefined}
            max={dateRange.max || undefined}
            onChange={(e) => onActivityDayChange(e.target.value)}
          />
        </label>
        <label className="users-field users-field-grow">
          <span className="users-field-label">Search</span>
          <input
            type="search"
            className="users-search"
            placeholder="Search name, type, facility…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <div className="users-field users-field-action">
          <span className="users-field-label" aria-hidden="true">&nbsp;</span>
          <button
            type="button"
            className="users-trend-btn"
            onClick={() => setTrendOpen(true)}
            disabled={!trendWindow.start}
            title="Line chart of daily logins over the last ~30 loaded days"
          >
            Daily trend
          </button>
        </div>
      </div>

      {dateRange.min && dateRange.max && (
        <p className="users-footnote">
          Loaded login data spans <strong>{dateRange.min}</strong> – <strong>{dateRange.max}</strong>.
          {userTypeFilter && (
            <> Filtered to user type <strong>{userTypeFilter}</strong>.</>
          )}
        </p>
      )}

      <div className="users-summary-strip">
        <span><strong>{sorted.length.toLocaleString()}</strong> users logged in</span>
        <span className="users-summary-muted">on {activityDay}</span>
      </div>

      {sorted.length === 0 ? (
        <p className="users-empty">
          No login activity for this day in the current scope and date range.
          {dateRange.min && activityDay < dateRange.min && (
            <> The earliest loaded day is {dateRange.min}.</>
          )}
          {dateRange.max && activityDay > dateRange.max && (
            <> The latest loaded day is {dateRange.max}.</>
          )}
          {' '}Try a date inside the loaded window or click Load Data with a wider range.
        </p>
      ) : (
        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr>
                <SortableTh label="User" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableTh label="Type" sortKey="userType" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableTh label="Facility" sortKey="facility" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableTh label="Specialty" sortKey="speciality" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableTh label="Logins" sortKey="logins" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
                <SortableTh label="Web" sortKey="web" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
                <SortableTh label="Mobile" sortKey="mobile" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
                <SortableTh label="Desktop" sortKey="desktop" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
                <SortableTh label="Location" sortKey="city" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.userId}>
                  <td>
                    <PersonContactHover userId={Number(r.userId)}>{r.name}</PersonContactHover>
                  </td>
                  <td>{r.userType}</td>
                  <td title={r.facility}>{r.facility}</td>
                  <td>{r.speciality || '—'}</td>
                  <td className="num">{platformTotal(r).toLocaleString()}</td>
                  <td className="num">{r.web || '—'}</td>
                  <td className="num">{r.mobile || '—'}</td>
                  <td className="num">{r.desktop || '—'}</td>
                  <td>{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {trendOpen && (
        <div className="users-modal-backdrop" onClick={() => setTrendOpen(false)}>
          <div
            className="users-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="users-trend-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="users-modal-header">
              <h3 id="users-trend-title">Daily login trend</h3>
              <button
                type="button"
                className="users-modal-close"
                onClick={() => setTrendOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="users-modal-body">
              <p className="users-modal-desc">
                {trendWindow.start && trendWindow.end
                  ? `${trendWindow.start} – ${trendWindow.end}`
                  : 'No loaded login data'}
                {userTypeFilter ? ` · type: ${userTypeFilter}` : ''}
                {' · '}last ~30 days ending on the selected activity day (clamped to loaded data)
              </p>
              {trendSeries.length === 0 ? (
                <p className="users-empty">No login data in this window.</p>
              ) : (
                <div className="users-trend-chart">
                  <Line data={trendChart.data} options={trendChart.options} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DailyLoginsTab;
